import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import Link from "next/link";
import { useInView } from "@hooks/useInView";
import styles from "./KolefnisutreikningurPage.module.scss";

export default function KolefnisutreikningurPage() {
    const t = useTranslations("KolefnisutreikningurPage");
    const [heroRef, heroInView] = useInView();
    const [showcase1Ref, showcase1InView] = useInView();
    const [showcase2Ref, showcase2InView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">
                    {"Kolefnisútreikningur - " + WEBPAGE_TITLE}
                </title>
                <meta
                    name="keywords"
                    content="Kolefnisútreikningur, kolefnisspor, CO2, LCA, EPD"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Kolefnisútreikningur`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={showcase1Ref}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${showcase1InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_calc_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_calc_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_calc_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line
                                                x1="16"
                                                y1="13"
                                                x2="8"
                                                y2="13"
                                            />
                                            <line
                                                x1="16"
                                                y1="17"
                                                x2="8"
                                                y2="17"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_fuel")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_energy")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_waste")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="20"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line
                                                x1="12"
                                                y1="20"
                                                x2="12"
                                                y2="4"
                                            />
                                            <line
                                                x1="6"
                                                y1="20"
                                                x2="6"
                                                y2="14"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_compare")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={showcase2Ref}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${showcase2InView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_lca_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_lca_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_lca_desc")}
                            </p>
                            <Link
                                className={styles.ctaBtn}
                                href="/kolefnisreiknir"
                            >
                                {t("try_lca_btn")} →
                            </Link>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.lcaCard}>
                                <img
                                    className={styles.lcaImg}
                                    src="/img/carbon/lca-demo-placeholder.svg"
                                    alt="EPD/LCA"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                    }}
                                />
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
